import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { formatCurrency } from "@/lib/utils";
import { Plus, Trash2 } from "lucide-react";
import {
  QuoteDeductionItemEntity,
  useCreateQuoteDeductionItem,
  useUpdateQuoteDeductionItem,
  useDeleteQuoteDeductionItem,
} from "@/hooks/useQuoteEntities";
import { useState, useCallback, useRef, useEffect } from "react";

interface QuoteTotals {
  netTotal: number;
  optionalTotal: number;
  discountAmount: number;
  discountedTotal: number;
  deductionsTotal: number;
  totalAfterDeductions: number;
  vatRateValue: number;
  vatAmount: number;
  grossTotal: number;
}

interface QuoteTotalsSummaryProps {
  totals: QuoteTotals;
  projectDiscountPercent: number;
  vatRate: "CH_8_1" | "NONE";
  quoteVersionId: string;
  deductionItems: QuoteDeductionItemEntity[];
  onDiscountChange: (value: number) => void;
  onDiscountBlur: () => void;
  onVatRateChange: (value: "CH_8_1" | "NONE") => void;
  readonly?: boolean;
}

export function QuoteTotalsSummary({
  totals,
  projectDiscountPercent,
  vatRate,
  quoteVersionId,
  deductionItems,
  onDiscountChange,
  onDiscountBlur,
  onVatRateChange,
  readonly,
}: QuoteTotalsSummaryProps) {
  const createDeduction = useCreateQuoteDeductionItem();
  const updateDeduction = useUpdateQuoteDeductionItem();
  const deleteDeduction = useDeleteQuoteDeductionItem();

  const [newlyCreatedId, setNewlyCreatedId] = useState<string | null>(null);

  const handleAddDeduction = () => {
    createDeduction.mutate(
      { quoteVersionId },
      {
        onSuccess: (data) => {
          setNewlyCreatedId(data.id);
        },
      },
    );
  };

  const handleUpdateDeduction = (
    id: string,
    updates: Partial<{ description: string; amount: number }>,
  ) => {
    updateDeduction.mutate({ id, updates });
  };

  const handleDeleteDeduction = (id: string) => {
    deleteDeduction.mutate({ id, quoteVersionId });
  };

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-base">Totals</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2 max-w-sm ml-auto">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              Net Total (excl. optional)
            </span>
            <span className="font-medium tabular-nums">
              {formatCurrency(totals.netTotal)}
            </span>
          </div>

          {totals.optionalTotal > 0 && (
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Optional Items Total</span>
              <span className="tabular-nums">
                {formatCurrency(totals.optionalTotal)}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center text-sm gap-4">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Discount</span>
              <Input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={projectDiscountPercent}
                onChange={(e) =>
                  onDiscountChange(parseFloat(e.target.value) || 0)
                }
                onBlur={onDiscountBlur}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => e.key === "Enter" && onDiscountBlur()}
                className="h-7 w-20 text-right tabular-nums [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                disabled={readonly}
              />
              <span className="text-muted-foreground">%</span>
            </div>
            {projectDiscountPercent > 0 && (
              <span className="font-medium text-green-600 tabular-nums">
                -{formatCurrency(totals.discountAmount)}
              </span>
            )}
          </div>

          {projectDiscountPercent > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Subtotal after Discount
              </span>
              <span className="font-medium tabular-nums">
                {formatCurrency(totals.discountedTotal)}
              </span>
            </div>
          )}

          {/* Deduction Items (Minus Line Items) */}
          {deductionItems.length > 0 && (
            <div className="space-y-1.5 pt-1">
              {deductionItems.map((item) => (
                <DeductionItemRow
                  key={item.id}
                  item={item}
                  autoFocus={item.id === newlyCreatedId}
                  onUpdate={(updates) =>
                    handleUpdateDeduction(item.id, updates)
                  }
                  onDelete={() => handleDeleteDeduction(item.id)}
                  readonly={readonly}
                />
              ))}
            </div>
          )}

          {!readonly && (
            <div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground -ml-2"
                onClick={handleAddDeduction}
                data-testid="add-deduction-button"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add deduction
              </Button>
            </div>
          )}

          {totals.deductionsTotal > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Subtotal after Deductions
              </span>
              <span className="font-medium tabular-nums">
                {formatCurrency(totals.totalAfterDeductions)}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center text-sm gap-4">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">VAT</span>
              <Select
                value={vatRate}
                onValueChange={onVatRateChange}
                disabled={readonly}
              >
                <SelectTrigger className="h-7 w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CH_8_1">8.1% (Switzerland)</SelectItem>
                  <SelectItem value="NONE">No VAT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {vatRate !== "NONE" && (
              <span className="font-medium tabular-nums">
                {formatCurrency(totals.vatAmount)}
              </span>
            )}
          </div>

          <Separator />

          <div className="flex justify-between text-base font-semibold">
            <span>Gross Total</span>
            <span className="tabular-nums">
              {formatCurrency(totals.grossTotal)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Deduction Item Row Component
interface DeductionItemRowProps {
  item: QuoteDeductionItemEntity;
  autoFocus?: boolean;
  onUpdate: (updates: Partial<{ description: string; amount: number }>) => void;
  onDelete: () => void;
  readonly?: boolean;
}

function DeductionItemRow({
  item,
  autoFocus,
  onUpdate,
  onDelete,
  readonly,
}: DeductionItemRowProps) {
  const [description, setDescription] = useState(item.description);
  const [amount, setAmount] = useState(item.amount);

  const fieldsRef = useRef({ description, amount });
  fieldsRef.current = { description, amount };
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const descriptionInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
      descriptionInputRef.current.select();
    }
  }, [autoFocus]);

  useEffect(() => {
    setDescription(item.description);
  }, [item.description]);

  useEffect(() => {
    setAmount(item.amount);
  }, [item.amount]);

  const saveAll = useCallback(() => {
    const f = fieldsRef.current;
    onUpdateRef.current({
      description: f.description,
      amount: f.amount,
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        saveAll();
      }
    },
    [saveAll],
  );

  return (
    <div className="flex items-center gap-2 text-sm">
      <Input
        ref={descriptionInputRef}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={saveAll}
        onKeyDown={handleKeyDown}
        placeholder="Description"
        className="h-7 flex-1 text-xs"
        disabled={readonly}
        data-testid="deduction-description"
      />
      <Input
        type="number"
        min={0}
        step={0.01}
        value={amount}
        onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
        onBlur={saveAll}
        onFocus={(e) => e.target.select()}
        onKeyDown={handleKeyDown}
        className="h-7 w-24 text-right text-xs tabular-nums [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
        disabled={readonly}
        data-testid="deduction-amount"
      />
      {!readonly && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
          data-testid="delete-deduction"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
      <span className="font-medium text-red-600 tabular-nums text-xs min-w-[60px] text-right">
        -{formatCurrency(amount)}
      </span>
    </div>
  );
}
